import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Outlet, useParams } from 'react-router-dom';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { Box, Divider, ListItemIcon, ListItemText, Typography, useTheme } from '@mui/material';
import {
  Info,
  Schedule,
  LocationOn,
  Image,
  Pool,
  Article
} from '@mui/icons-material';
import { Can } from '@casl/react';

import { DataBox } from '@frontend/features/common/components/DataBox';
import { SideList, SideListItemButton } from '@frontend/features/common/components/SideList';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { BackButton } from '@frontend/features/common/components/BackButton';
import { IconAvatar } from '@frontend/features/common/components/IconAvatar';

import { routes } from '../routes';

export const HotelContentViewPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { ability } = useAuth();
  const { hotelId } = useParams<{ hotelId: string }>();

  const isViewPage = false;

  // TODO: Replace with actual hotel data from API
  const hotelData = {
    name: 'Hotel Example',
    country: 'España',
    city: 'Madrid'
  };

  return (
    <Box
      display="flex"
      gap={isViewPage ? 0 : 8}
      sx={{
        height: 'calc(100vh - 64px)',
        backgroundColor: theme.palette.paper.dark,
      }}
      overflow="hidden"
    >
      <OverlayScrollbarsComponent
        style={{
          minWidth: 300,
          backgroundColor: theme.palette.paper.dark,
          boxSizing: 'border-box',
          height: '100%',
          boxShadow: theme.shadows[1],
        }}
        className="overlayscrollbars-react"
        options={{ scrollbars: { autoHide: 'scroll', theme: 'os-theme-light' } }}
        defer
      >
        <Box display="flex" flexDirection="column" alignItems="flex-start" alignSelf="stretch" p={2} gap={3}>
          <Box display="flex" gap={1} alignItems="center" mt={1}>
            <IconAvatar size="medium" backgroundColor={theme.palette.divider}>
              <LocationOn htmlColor={theme.palette.common.white} />
            </IconAvatar>
            <DataBox
              sx={{
                gap: 0,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                <Typography variant="subtitle2" color="text.primary">
                  {hotelData.name}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {hotelData.country}
              </Typography>
            </DataBox>
          </Box>

          <Box sx={{ width: '100%' }}>
            <Divider aria-hidden="true" textAlign="left" sx={{ mb: 3 }} />

            <SideList aria-label={`hotel content view menu`} dense disablePadding>
              <Can I="view" a="ContentManager" ability={ability}>
                
                <SideListItemButton
                  selected={Boolean(
                    location.pathname === `${routes.dashboard.hotelContentView.root.replace(':hotelId', hotelId || '')}`,
                  )}
                  onClick={() =>
                    navigate(`${routes.dashboard.hotelContentView.root.replace(':hotelId', hotelId || '')}`)
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Info />
                  </ListItemIcon>
                  <ListItemText primary={t('hotelContent.view.tabs.general')} />
                </SideListItemButton>
                
                <SideListItemButton
                  selected={Boolean(
                    location.pathname.includes(
                      `${routes.dashboard.hotelContentView.root.replace(':hotelId', hotelId || '')}/${routes.dashboard.hotelContentView.checkin}`,
                    ),
                  )}
                  onClick={() =>
                    navigate(
                      `${routes.dashboard.hotelContentView.root.replace(':hotelId', hotelId || '')}/${routes.dashboard.hotelContentView.checkin}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Schedule />
                  </ListItemIcon>
                  <ListItemText primary={t('hotelContent.view.tabs.checkinCheckout')} />
                </SideListItemButton>

                <SideListItemButton
                  selected={Boolean(
                    location.pathname.includes(
                      `${routes.dashboard.hotelContentView.root.replace(':hotelId', hotelId || '')}/${routes.dashboard.hotelContentView.location}`,
                    ),
                  )}
                  onClick={() =>
                    navigate(
                      `${routes.dashboard.hotelContentView.root.replace(':hotelId', hotelId || '')}/${routes.dashboard.hotelContentView.location}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText primary={t('hotelContent.view.tabs.location')} />
                </SideListItemButton>

                <SideListItemButton
                  selected={Boolean(
                    location.pathname.includes(
                      `${routes.dashboard.hotelContentView.root.replace(':hotelId', hotelId || '')}/${routes.dashboard.hotelContentView.images}`,
                    ),
                  )}
                  onClick={() =>
                    navigate(
                      `${routes.dashboard.hotelContentView.root.replace(':hotelId', hotelId || '')}/${routes.dashboard.hotelContentView.images}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Image />
                  </ListItemIcon>
                  <ListItemText primary={t('hotelContent.view.tabs.images')} />
                </SideListItemButton>

                <SideListItemButton
                  selected={Boolean(
                    location.pathname.includes(
                      `${routes.dashboard.hotelContentView.root.replace(':hotelId', hotelId || '')}/${routes.dashboard.hotelContentView.amenities}`,
                    ),
                  )}
                  onClick={() =>
                    navigate(
                      `${routes.dashboard.hotelContentView.root.replace(':hotelId', hotelId || '')}/${routes.dashboard.hotelContentView.amenities}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Pool />
                  </ListItemIcon>
                  <ListItemText primary={t('hotelContent.view.tabs.amenities')} />
                </SideListItemButton>

                <SideListItemButton
                  selected={Boolean(
                    location.pathname.includes(
                      `${routes.dashboard.hotelContentView.root.replace(':hotelId', hotelId || '')}/${routes.dashboard.hotelContentView.content}`,
                    ),
                  )}
                  onClick={() =>
                    navigate(
                      `${routes.dashboard.hotelContentView.root.replace(':hotelId', hotelId || '')}/${routes.dashboard.hotelContentView.content}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Article />
                  </ListItemIcon>
                  <ListItemText primary={t('hotelContent.view.tabs.content')} />
                </SideListItemButton>

              </Can>
            </SideList>
          </Box>
        </Box>
      </OverlayScrollbarsComponent>

      {!isViewPage ? (
        <Box
          sx={{
            overflowX: 'auto',
            width: '100%',
            pr: 2,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              mt: 2,
              mb: 2,
            }}
          >
            <BackButton backRoute={routes.dashboard.hotelContentManagement.root} />
          </Box>

          <Outlet />
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Outlet />
        </Box>
      )}
    </Box>
  );
};